import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, Platform } from "@ionic/angular";
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LogicLog } from 'src/model/class/general classes/logic-log';
import { Notific } from 'src/model/class/general classes/notific';
import { NotificationRequest_ShowPopup } from 'src/model/class/general classes/notification-request';
import { PopupAlarm } from 'src/model/class/general classes/popup-alarm';
import { StaticClass } from 'src/components/shared-components/class/general-classes/static-classes';
import { ConnectionType } from 'src/components/shared-components/enum/enumSettings';
import { GenericStoreApiData } from 'src/components/shared-components/interfaces/generic-store-api-data';
import { iConnToolbarColour } from 'src/components/shared-components/interfaces/iConnToolbarColour';
import { LogicsService } from 'src/service/logics.service';
import { NetworkService } from 'src/service/network.service';
import { NotificationService } from 'src/service/notification.service';
import { TranslationService } from 'src/service/translation.service';
import { listNotification } from 'src/state/selectors/notifications.selectors';

@Component({
    selector: 'app-list-alarms',
    templateUrl: './list-alarms.component.html',
    styleUrls: ['./list-alarms.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListAlarmsComponent implements OnInit, OnDestroy, OnChanges, iConnToolbarColour {
  @Input() alarmInfo: Subject<NotificationRequest_ShowPopup>;
  @Input() solveResetValue: string;
  @Output() selectedAlarmEmitter = new EventEmitter<PopupAlarm>();

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectListNotificationUserSub: Subscription = null;

  private _alarmInfoSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;

  private _allActiveAlarms: Array<LogicLog> = new Array<LogicLog>();
  private _notifications: Array<Notific> = null;
  public popupAlarm: PopupAlarm = null;
  public activeAlarms: Array<LogicLog> = new Array<LogicLog>();

  public isApp: boolean = false;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  constructor(
    private _translationService: TranslationService,
    private _modalController: ModalController,
    private _logicService: LogicsService,
    private _store: Store,
    private _notificService: NotificationService,
    private _platform: Platform,
    private _networkService: NetworkService
  ) {
    this._notificService.Init();
    this.loadNotifications();
    this.isApp = this._platform.is("ios") || this._platform.is("android");
  }

  ngOnInit(): void {
    this.subscribeNetworkServiceData();
    this.onClickOnIonBackdrop();
    this.popupAlarm = new PopupAlarm();
    let timer = setTimeout(() => {
      this.subscribeAlarmInfo();
      clearTimeout(timer);
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.solveResetValue != "") {
      this._alarmInfoSubscription?.unsubscribe();
      this.subscribeAlarmInfo();
    }
  }

  ngOnDestroy(): void {
    this._networkServiceSubscription?.unsubscribe();
    this._alarmInfoSubscription?.unsubscribe();
    this.unsubscribeStoreSelect();
  }

  /**
   * When clicked, it shows the information of the selected alarm, 
   * the alarms still active of the same type and the buttons to reset or view it.
   * @param alarm alarm to display
   */
  public clickToShowAlarm(alarm: LogicLog): void {
    this.popupAlarm.selectedAlarm = alarm;
    this.popupAlarm.otherAlarmsPresent = this.activeAlarms.length > 1;
    this.popupAlarm.duplicateAlarms = [];
    this.popupAlarm.duplicateAlarms = [...this._allActiveAlarms.filter((alarmToAdd: LogicLog) => (alarmToAdd.idbigLogic === alarm.idbigLogic && alarmToAdd.timeStamp != alarm.timeStamp))];
    this.selectedAlarmEmitter.emit(this.popupAlarm);
  }

  /**
   * Returns the formatted date.
   */
  public formatDate(date: string) {
    return formatDate(date, "mediumDate", this._translationService.currentLanguage);
  }

  /**
   * Returns the formatted time. 
   */
  public formatTime(date: string) {
    return formatDate(date, "mediumTime", this._translationService.currentLanguage);
  }

  /**
   * If the active alarms are all closable, it allows to close the ion-modal.
   */
  public onClickDismissModal() {
    let closable: boolean = true;
    for (let i = 0; i < this.activeAlarms.length; i++) {
      let notific = this._notifications.find(notific => notific.id == this.activeAlarms[i].notificationId);
      if (!notific.allowPopupClosure) {
        closable = false;
      }
    }

    if (closable) {
      this._modalController.dismiss();
    }
  }

  /**
   * Subscribte to AlarmInfo
   */
  private subscribeAlarmInfo(): void {
    this._alarmInfoSubscription = this.alarmInfo.subscribe(async () => {
      const alarms = await this._logicService.GetActiveAlarms(true, 0);
      if (alarms) {
        if (alarms instanceof Array) {
          this._allActiveAlarms = alarms;
        } else {
          this._allActiveAlarms.push(alarms);
        }
        this.setAlarmList();
      } else {
        await this._modalController.dismiss();
      }
    });
  }

  /**
   * Set the list of main alarms.
   */
  private setAlarmList(): void {
    this.activeAlarms = [];

    for (let i = 0; i < this._allActiveAlarms.length; i++) {
      let notific = this._notifications.find(notif => notif.id == this._allActiveAlarms[i].notificationId);

      if (notific.popupCustomTitle != "") {
        this._allActiveAlarms[i].description = notific.popupCustomTitle;
      }
      if (this.activeAlarms.findIndex((alarm: LogicLog) => alarm.idbigLogic === this._allActiveAlarms[i].idbigLogic) < 0) {
        this.activeAlarms.push(this._allActiveAlarms[i]);
      }
    }
    this.clickToShowAlarm(this.activeAlarms[0]);
  }

  /**
   * Adds an event listener to ion-modal ion-backdrop
   * so that once clicked it will close the modal if 
   * all closable alarms are present.
   */
  private onClickOnIonBackdrop(): void {
    const modal = document.getElementsByTagName("ion-modal");
    const backdrop = modal[0].shadowRoot.querySelector('ion-backdrop');
    backdrop.stopPropagation = false;
    backdrop.addEventListener('click', () => {
      this.onClickDismissModal();
    });
  }
  /**
   * Upload all notifications from the store.
   */
  private loadNotifications(): void {
    this._selectListNotificationUserSub = this._store
      .select(listNotification)
      .pipe(
        filter(
          (listNotificationObject: GenericStoreApiData) => listNotificationObject !== null && listNotificationObject !== undefined
        ),
        filter(
          (listNotificationObject: GenericStoreApiData) =>
            listNotificationObject.loaded
        )
      )
      .subscribe(async (listNotificationObject: GenericStoreApiData) => {
        this._notifications = (listNotificationObject.data?.data || []);
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectListNotificationUserSub.unsubscribe();
  }
  //#endregion

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
